import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, ArrowLeft, ArrowRight, Check, Brain, Sparkles, AlertTriangle,
  CheckCircle2, XCircle, RotateCcw, Save,
} from 'lucide-react';
import { useOrg } from '../../hooks/useOrg';
import { MOCA, flattenSteps, scoreAssessment } from '../../lib/assessments/moca';
import { saveAssessment } from '../../lib/assessments/storage';
import {
  InteractiveIntro,
  InteractiveTrail,
  InteractiveNaming,
  InteractiveMemory,
  InteractiveDigitForward,
  InteractiveDigitBackward,
  InteractiveVigilance,
  InteractiveSerial7,
  InteractiveFluency,
  InteractiveRecall,
  InteractiveOrientation,
  InteractiveProviderHandoff,
} from './MocaInteractive';
import './MocaConversational.css';

// Conversational MoCA flow.
//
// One step at a time, full-bleed, no app shell. The administrator reads the
// prompt aloud, observes the patient, then records the score on screen. The
// flow scores each step as it goes so a partial save still has a meaningful
// running total.

const ALL_STEPS = flattenSteps(MOCA);

// -----------------------------------------------------------------------
// Per-kind step body — renders the instruction card + the score capture.
// Each returns { score, observations } via the onChange callback.
// -----------------------------------------------------------------------

function StepShell({ children }) {
  return <div className="moca-step-body">{children}</div>;
}

function InstructionCard({ instruction, example, subtext }) {
  return (
    <div className="moca-instruction">
      <div className="moca-instruction-eyebrow">Say to the patient</div>
      <p className="moca-instruction-body">{instruction}</p>
      {example && (
        <div className="moca-instruction-example">
          {example}
        </div>
      )}
      {subtext && <p className="moca-instruction-subtext">{subtext}</p>}
    </div>
  );
}

function ScoringRule({ rule }) {
  const [open, setOpen] = useState(false);
  if (!rule) return null;
  return (
    <div className="moca-rule">
      <button onClick={() => setOpen(o => !o)} className="moca-rule-toggle">
        {open ? '−' : '+'}  How is this scored?
      </button>
      {open && <div className="moca-rule-body">{rule}</div>}
    </div>
  );
}

function BinaryStep({ step, answer, onChange }) {
  const score = answer?.score;
  return (
    <StepShell>
      <InstructionCard instruction={step.instruction} example={step.example} />
      <div className="moca-section-label">How did the patient do?</div>
      <div className="moca-binary">
        <button
          type="button"
          onClick={() => onChange({ score: 0 })}
          className={`moca-choice moca-choice-no ${score === 0 ? 'is-selected' : ''}`}
        >
          <XCircle size={20} strokeWidth={2.2} />
          <span className="moca-choice-label">Incorrect / partial</span>
          <span className="moca-choice-points">0 pts</span>
        </button>
        <button
          type="button"
          onClick={() => onChange({ score: 1 })}
          className={`moca-choice moca-choice-yes ${score === 1 ? 'is-selected' : ''}`}
        >
          <CheckCircle2 size={20} strokeWidth={2.2} />
          <span className="moca-choice-label">Correct</span>
          <span className="moca-choice-points">+1 pt</span>
        </button>
      </div>
      <ScoringRule rule={step.scoring_rule} />
    </StepShell>
  );
}

function BinaryMultiStep({ step, answer, onChange }) {
  const selected = answer?.items || {};
  const toggle = (id) => {
    const next = { ...selected, [id]: !selected[id] };
    const score = Object.values(next).filter(Boolean).length;
    onChange({ score, items: next });
  };
  const total = Object.values(selected).filter(Boolean).length;
  return (
    <StepShell>
      <InstructionCard instruction={step.instruction} />
      <div className="moca-section-label">
        Mark the items the patient got correct
        <span className="moca-running">({total}/{step.max})</span>
      </div>
      <div className="moca-multi">
        {step.items.map(it => {
          const on = !!selected[it.id];
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => toggle(it.id)}
              className={`moca-multi-item ${on ? 'is-on' : ''}`}
            >
              <div className={`moca-check ${on ? 'is-on' : ''}`}>
                {on && <Check size={13} strokeWidth={3} />}
              </div>
              <div className="moca-multi-text">
                <div className="moca-multi-label">{it.label}</div>
                {it.hint && <div className="moca-multi-hint">{it.hint}</div>}
              </div>
              <div className="moca-multi-pts">+1</div>
            </button>
          );
        })}
      </div>
      <ScoringRule rule={step.scoring_rule} />
    </StepShell>
  );
}

function VigilanceStep({ step, answer, onChange }) {
  const errors = answer?.errors ?? '';
  const setErrors = (v) => {
    const n = v === '' ? null : Math.max(0, parseInt(v, 10) || 0);
    const score = n != null && n < 2 ? 1 : 0;
    onChange({ score, errors: n });
  };
  return (
    <StepShell>
      <InstructionCard instruction={step.instruction} />
      <div className="moca-letters">{step.letters}</div>
      <div className="moca-section-label">Total number of errors</div>
      <div className="moca-numeric">
        <input
          type="number"
          min="0"
          value={errors ?? ''}
          onChange={e => setErrors(e.target.value)}
          placeholder="e.g. 0"
          className="moca-numeric-input"
        />
        <div className="moca-numeric-helper">
          {errors === null || errors === ''
            ? 'Count both missed As and incorrect taps.'
            : errors < 2
              ? <span className="text-success">≤ 1 error · scores 1 pt</span>
              : <span className="text-warning">{errors} errors · scores 0 pts</span>}
        </div>
      </div>
      <ScoringRule rule={step.scoring_rule} />
    </StepShell>
  );
}

function Serial7Step({ step, answer, onChange }) {
  const got = answer?.got || {};
  const toggle = (n) => {
    const next = { ...got, [n]: !got[n] };
    const correct = Object.values(next).filter(Boolean).length;
    // Map correct count → score (4-5=3, 2-3=2, 1=1, 0=0)
    let score = 0;
    if (correct >= 4) score = 3;
    else if (correct >= 2) score = 2;
    else if (correct === 1) score = 1;
    onChange({ score, got: next, correctCount: correct });
  };
  const correct = Object.values(got).filter(Boolean).length;
  return (
    <StepShell>
      <InstructionCard instruction={step.instruction} subtext='Mark each target the patient produced — even if it was the right step from a previous wrong answer.' />
      <div className="moca-section-label">
        Check each target the patient reached
        <span className="moca-running">({correct} correct → {answer?.score ?? 0}/3)</span>
      </div>
      <div className="moca-serial7">
        {step.targets.map((n, i) => {
          const on = !!got[n];
          return (
            <button
              key={n}
              type="button"
              onClick={() => toggle(n)}
              className={`moca-s7 ${on ? 'is-on' : ''}`}
            >
              <div className={`moca-check ${on ? 'is-on' : ''}`}>
                {on && <Check size={13} strokeWidth={3} />}
              </div>
              <div className="moca-s7-num">{n}</div>
              <div className="moca-s7-sub">{i === 0 ? '100 − 7' : `${step.targets[i - 1]} − 7`}</div>
            </button>
          );
        })}
      </div>
      <ScoringRule rule={step.scoring_rule} />
    </StepShell>
  );
}

function FluencyStep({ step, answer, onChange }) {
  const count = answer?.count ?? '';
  const set = (v) => {
    const n = v === '' ? null : Math.max(0, parseInt(v, 10) || 0);
    const score = n != null && n >= step.threshold ? 1 : 0;
    onChange({ score, count: n });
  };
  return (
    <StepShell>
      <InstructionCard instruction={step.instruction} subtext={step.subtext} />
      <div className="moca-section-label">Unique acceptable F-words produced in 60 seconds</div>
      <div className="moca-numeric">
        <input
          type="number"
          min="0"
          value={count ?? ''}
          onChange={e => set(e.target.value)}
          placeholder="e.g. 12"
          className="moca-numeric-input"
        />
        <div className="moca-numeric-helper">
          {count === null || count === ''
            ? `Threshold: ${step.threshold} or more = 1 pt.`
            : count >= step.threshold
              ? <span className="text-success">{count} words · scores 1 pt</span>
              : <span className="text-warning">{count} words · scores 0 pts (need ≥ {step.threshold})</span>}
        </div>
      </div>
      <ScoringRule rule={step.scoring_rule} />
    </StepShell>
  );
}

function MemoryTrialStep({ step, answer, onChange }) {
  // Two trials, no scoring. Track which words were recalled in each trial
  // for clinical interpretation later.
  const trial1 = answer?.trial1 || {};
  const trial2 = answer?.trial2 || {};
  const toggle = (which, w) => {
    const next = { ...(which === 1 ? trial1 : trial2), [w]: !((which === 1 ? trial1 : trial2)[w]) };
    onChange({
      score: 0,
      trial1: which === 1 ? next : trial1,
      trial2: which === 2 ? next : trial2,
    });
  };
  const renderTrial = (n, state) => (
    <div className="moca-trial">
      <div className="moca-trial-label">Trial {n}</div>
      <div className="moca-trial-words">
        {step.words.map(w => {
          const on = !!state[w];
          return (
            <button
              key={w}
              type="button"
              onClick={() => toggle(n, w)}
              className={`moca-word ${on ? 'is-on' : ''}`}
            >
              {w}
            </button>
          );
        })}
      </div>
    </div>
  );
  return (
    <StepShell>
      <InstructionCard
        instruction={step.instruction}
        subtext={step.subtext}
      />
      <div className="moca-section-label">Tap each word the patient recalled · no points</div>
      {renderTrial(1, trial1)}
      {renderTrial(2, trial2)}
    </StepShell>
  );
}

function RecallStep({ step, answer, onChange }) {
  const recalled = answer?.recalled || {};
  const toggle = (w) => {
    const next = { ...recalled, [w]: !recalled[w] };
    const score = Object.values(next).filter(Boolean).length;
    onChange({ score, recalled: next });
  };
  const score = Object.values(recalled).filter(Boolean).length;
  return (
    <StepShell>
      <InstructionCard instruction={step.instruction} subtext={step.subtext} />
      <div className="moca-section-label">
        Words recalled with no cue
        <span className="moca-running">({score}/5)</span>
      </div>
      <div className="moca-recall-grid">
        {step.words.map(w => {
          const on = !!recalled[w];
          return (
            <button
              key={w}
              type="button"
              onClick={() => toggle(w)}
              className={`moca-recall ${on ? 'is-on' : ''}`}
            >
              <div className={`moca-check ${on ? 'is-on' : ''}`}>
                {on && <Check size={13} strokeWidth={3} />}
              </div>
              <span>{w}</span>
            </button>
          );
        })}
      </div>
      <ScoringRule rule={step.scoring_rule} />
    </StepShell>
  );
}

function IntroStep({ step }) {
  return (
    <StepShell>
      <div className="moca-intro">
        <div className="moca-intro-icon">
          <Brain size={28} strokeWidth={2.2} />
        </div>
        <h2 className="moca-intro-title">{step.headline}</h2>
        <p className="moca-intro-body">{step.body}</p>
        <ul className="moca-intro-notes">
          {step.notes.map(n => (
            <li key={n}><span className="moca-intro-dot" />{n}</li>
          ))}
        </ul>
      </div>
    </StepShell>
  );
}

// -----------------------------------------------------------------------
// Main flow
// -----------------------------------------------------------------------

const PHASE_PATIENT  = 'patient';
const PHASE_STEPS    = 'steps';
const PHASE_COMPLETE = 'complete';

// Which step IDs are eligible for the interactive (self-administered) variant.
// Anything not listed falls back to InteractiveProviderHandoff in interactive
// mode, which still scores via provider but speaks to the patient.
const INTERACTIVE_KINDS = new Set([
  'trail-making',
  'naming-all',
  'memory-read',
  'digit-forward',
  'digit-backward',
  'vigilance',
  'serial-7',
  'fluency',
  'recall-uncued',
  'orientation-all',
]);

// Patient-facing copy for steps that fall back to provider scoring in
// interactive mode (cube, clock, sentences, abstraction). Keyed by step.id.
const HANDOFF_COPY = {
  'cube-copy': {
    heading: 'Draw this cube on paper as accurately as you can.',
    body: "Then show it to your provider — they'll mark a quick score and you'll move on.",
  },
  'clock-contour': {
    heading: 'On paper, draw a clock face showing "ten past eleven".',
    body: 'Use a full circle, all twelve numbers, and two hands. Show it to your provider when you\'re done.',
  },
  'clock-numbers': {
    heading: 'Use the same drawing — your provider will check the numbers.',
    body: 'No need to redraw. Your provider will mark whether the numbers are in the right places.',
  },
  'clock-hands': {
    heading: 'Same drawing — your provider will check the clock hands.',
    body: 'They\'ll mark whether the hour and minute hands look right.',
  },
  'sentence-1': {
    heading: 'Listen to your provider read a sentence, then repeat it back exactly.',
    body: 'They\'ll mark how close your repetition was.',
  },
  'sentence-2': {
    heading: 'One more sentence — listen, then repeat it back exactly.',
    body: 'Same idea as the previous one.',
  },
  'abstraction-pairs': {
    heading: 'How are these things alike?',
    body: 'Your provider will read you two pairs of words. For each pair, tell them how the two are alike. They\'ll mark your answer.',
  },
};

export default function MocaConversational() {
  const navigate = useNavigate();
  const { currentOrg } = useOrg();

  const [phase, setPhase] = useState(PHASE_PATIENT);
  const [mode, setMode] = useState('interactive'); // 'interactive' | 'provider'
  const [patientName, setPatientName] = useState('');
  const [patientNotes, setPatientNotes] = useState('');
  const [educationYears, setEducationYears] = useState('');
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const currentStep = ALL_STEPS[stepIndex];
  const isLast = stepIndex === ALL_STEPS.length - 1;
  const isFirst = stepIndex === 0;

  const scoredSteps = ALL_STEPS.filter(s => s.max > 0);
  const completedScored = scoredSteps.filter(s => answers[s.id]?.score != null).length;

  const progress = (stepIndex + 1) / ALL_STEPS.length;
  const runningResult = useMemo(() => scoreAssessment(answers, {
    educationYears: educationYears === '' ? null : Number(educationYears),
  }), [answers, educationYears]);

  const setAnswer = useCallback((partial) => {
    setAnswers(prev => ({
      ...prev,
      [currentStep.id]: { ...(prev[currentStep.id] || {}), ...partial },
    }));
  }, [currentStep]);

  const goNext = useCallback(() => {
    if (isLast) {
      setPhase(PHASE_COMPLETE);
      return;
    }
    setStepIndex(i => Math.min(i + 1, ALL_STEPS.length - 1));
  }, [isLast]);

  const goBack = useCallback(() => {
    setStepIndex(i => Math.max(i - 1, 0));
  }, []);

  const handleExit = () => {
    const hasWork = Object.keys(answers).length > 0 || patientName;
    if (hasWork && !window.confirm('Exit without saving this assessment?')) return;
    navigate('/app/tbi-assessments');
  };

  const handleSave = () => {
    const record = saveAssessment({
      orgId: currentOrg?.id,
      type: 'moca',
      patientName: patientName || 'Anonymous patient',
      patientNotes,
      answers,
      result: runningResult,
      educationYears: educationYears === '' ? null : Number(educationYears),
      mode,
    });
    navigate(`/app/tbi-assessments/${record.id}`, { replace: true });
  };

  // Keyboard nav — ← / → / Enter
  useEffect(() => {
    if (phase !== PHASE_STEPS) return;
    const handler = (e) => {
      if (e.target.matches('input, textarea, button')) return;
      if (e.key === 'ArrowRight' || e.key === 'Enter') goNext();
      if (e.key === 'ArrowLeft') goBack();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [phase, goNext, goBack]);

  // -----------------------------------------------------------------------
  // PATIENT phase — capture name + education
  // -----------------------------------------------------------------------
  if (phase === PHASE_PATIENT) {
    return (
      <div className="moca-shell">
        <header className="moca-topbar">
          <button onClick={handleExit} className="moca-exit">
            <X size={15} strokeWidth={2.2} /> Exit
          </button>
          <div className="moca-topbar-title">
            <Sparkles size={13} strokeWidth={2.2} /> MoCA · v{MOCA.version}
          </div>
          <div style={{ width: 80 }} />
        </header>

        <main className="moca-main moca-main-narrow">
          <div className="moca-eyebrow">
            <span className="moca-dot" /> NEW ASSESSMENT
          </div>
          <h1 className="moca-title">
            Who are we screening <em>today?</em>
          </h1>
          <p className="moca-sub">
            We'll save the result to this patient's record. The optional
            education field unlocks the standard +1 point bonus for ≤ 12 years
            of schooling.
          </p>

          <div className="moca-mode" role="radiogroup" aria-label="Assessment mode">
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'interactive'}
              onClick={() => setMode('interactive')}
              className={`moca-mode-card ${mode === 'interactive' ? 'is-selected' : ''}`}
            >
              <div className="moca-mode-eyebrow">Recommended</div>
              <div className="moca-mode-title">Patient on device</div>
              <div className="moca-mode-sub">
                Self-administered. The patient taps and types their answers right
                here. Provider scores a few items that need to be observed.
              </div>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'provider'}
              onClick={() => setMode('provider')}
              className={`moca-mode-card ${mode === 'provider' ? 'is-selected' : ''}`}
            >
              <div className="moca-mode-eyebrow">Classic</div>
              <div className="moca-mode-title">Provider administered</div>
              <div className="moca-mode-sub">
                You read each prompt aloud to the patient and record the score
                yourself, exactly like the paper MoCA.
              </div>
            </button>
          </div>

          <div className="moca-form">
            <label className="moca-field">
              <span className="moca-field-label">Patient name</span>
              <input
                value={patientName}
                onChange={e => setPatientName(e.target.value)}
                placeholder="e.g., Maria González"
                className="moca-input"
                autoFocus
              />
            </label>

            <label className="moca-field">
              <span className="moca-field-label">
                Years of formal education <span className="moca-optional">(optional)</span>
              </span>
              <input
                type="number"
                min="0"
                max="30"
                value={educationYears}
                onChange={e => setEducationYears(e.target.value)}
                placeholder="e.g., 12"
                className="moca-input"
              />
              <span className="moca-field-help">
                The MoCA awards +1 point to scores below 30 for patients with ≤ 12 years of education.
              </span>
            </label>

            <label className="moca-field">
              <span className="moca-field-label">
                Clinical note <span className="moca-optional">(optional)</span>
              </span>
              <textarea
                value={patientNotes}
                onChange={e => setPatientNotes(e.target.value)}
                placeholder="Mechanism of injury, time since incident, presenting symptoms…"
                rows={3}
                className="moca-input moca-textarea"
              />
            </label>
          </div>

          <div className="moca-actions">
            <div />
            <button
              onClick={() => setPhase(PHASE_STEPS)}
              className="moca-btn moca-btn-primary"
            >
              Begin assessment <ArrowRight size={14} strokeWidth={2.5} />
            </button>
          </div>
        </main>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // COMPLETE phase — review + save
  // -----------------------------------------------------------------------
  if (phase === PHASE_COMPLETE) {
    return (
      <div className="moca-shell">
        <header className="moca-topbar">
          <button onClick={() => setPhase(PHASE_STEPS)} className="moca-exit">
            <ArrowLeft size={15} strokeWidth={2.2} /> Back to questions
          </button>
          <div className="moca-topbar-title">
            <Sparkles size={13} strokeWidth={2.2} /> Review
          </div>
          <div style={{ width: 140 }} />
        </header>

        <main className="moca-main moca-main-narrow">
          <div className="moca-eyebrow">
            <span className="moca-dot" /> READY TO SAVE
          </div>
          <h1 className="moca-title">
            {runningResult.isNormal ? <>Result: <em>within normal range</em>.</> : <>Result: <em>below threshold</em>.</>}
          </h1>

          <div className="moca-result-hero">
            <div className="moca-result-big">
              <div className="moca-result-num">{runningResult.total}</div>
              <div className="moca-result-of">/ {runningResult.max}</div>
            </div>
            <div className="moca-result-meta">
              <div>Normal ≥ {runningResult.normalThreshold}</div>
              {runningResult.bonusApplied && <div>+1 education bonus applied</div>}
              <div>Completed {completedScored}/{scoredSteps.length} scored items</div>
            </div>
          </div>

          <div className="moca-section-label">Per-section breakdown</div>
          <div className="moca-breakdown">
            {Object.entries(runningResult.bySection)
              .filter(([, s]) => s.max > 0)
              .map(([id, s]) => (
                <div key={id} className="moca-bd-row">
                  <div className="moca-bd-name">{s.title}</div>
                  <div className="moca-bd-bar">
                    <div
                      className="moca-bd-fill"
                      style={{ width: `${(s.score / s.max) * 100}%` }}
                    />
                  </div>
                  <div className="moca-bd-score">{s.score} / {s.max}</div>
                </div>
              ))}
          </div>

          {completedScored < scoredSteps.length && (
            <div className="moca-warning">
              <AlertTriangle size={14} strokeWidth={2.2} />
              <div>
                {scoredSteps.length - completedScored} scored item{scoredSteps.length - completedScored === 1 ? '' : 's'} {scoredSteps.length - completedScored === 1 ? 'was' : 'were'} skipped. You can go back to complete them, or save now and finish later.
              </div>
            </div>
          )}

          <div className="moca-actions">
            <button onClick={() => { setPhase(PHASE_STEPS); setStepIndex(0); }} className="moca-btn moca-btn-ghost">
              <RotateCcw size={13} strokeWidth={2.5} /> Review all steps
            </button>
            <button onClick={handleSave} className="moca-btn moca-btn-primary">
              <Save size={14} strokeWidth={2.5} /> Save assessment
            </button>
          </div>
        </main>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // STEPS phase — one step at a time
  // -----------------------------------------------------------------------
  const stepAnswer = answers[currentStep.id];
  const hasScore = stepAnswer?.score != null || currentStep.kind === 'intro' || currentStep.kind === 'memory-trial';

  // Pick the renderer. In interactive mode, eligible steps get a patient-facing
  // self-administered component; ineligible ones get the provider handoff card.
  // In provider mode, every step uses the classic "Say to the patient" UI.
  let body;
  if (mode === 'interactive') {
    if (currentStep.kind === 'intro') {
      body = <InteractiveIntro />;
    } else if (INTERACTIVE_KINDS.has(currentStep.id)) {
      switch (currentStep.id) {
        case 'trail-making':
          body = <InteractiveTrail answer={stepAnswer} onChange={setAnswer} />; break;
        case 'naming-all':
          body = <InteractiveNaming answer={stepAnswer} onChange={setAnswer} />; break;
        case 'memory-read':
          body = <InteractiveMemory answer={stepAnswer} onChange={setAnswer} />; break;
        case 'digit-forward':
          body = <InteractiveDigitForward answer={stepAnswer} onChange={setAnswer} />; break;
        case 'digit-backward':
          body = <InteractiveDigitBackward answer={stepAnswer} onChange={setAnswer} />; break;
        case 'vigilance':
          body = <InteractiveVigilance answer={stepAnswer} onChange={setAnswer} />; break;
        case 'serial-7':
          body = <InteractiveSerial7 step={currentStep} answer={stepAnswer} onChange={setAnswer} />; break;
        case 'fluency':
          body = <InteractiveFluency step={currentStep} answer={stepAnswer} onChange={setAnswer} />; break;
        case 'recall-uncued':
          body = <InteractiveRecall answer={stepAnswer} onChange={setAnswer} />; break;
        case 'orientation-all':
          body = <InteractiveOrientation answer={stepAnswer} onChange={setAnswer} />; break;
        default: body = null;
      }
    } else {
      const copy = HANDOFF_COPY[currentStep.id] || {
        heading: currentStep.headline,
        body: currentStep.instruction,
      };
      body = (
        <InteractiveProviderHandoff
          step={currentStep}
          answer={stepAnswer}
          onChange={setAnswer}
          patientHeading={copy.heading}
          patientBody={copy.body}
        />
      );
    }
  } else {
    switch (currentStep.kind) {
      case 'intro':
        body = <IntroStep step={currentStep} />; break;
      case 'binary':
        body = <BinaryStep step={currentStep} answer={stepAnswer} onChange={setAnswer} />; break;
      case 'binary-multi':
        body = <BinaryMultiStep step={currentStep} answer={stepAnswer} onChange={setAnswer} />; break;
      case 'vigilance':
        body = <VigilanceStep step={currentStep} answer={stepAnswer} onChange={setAnswer} />; break;
      case 'serial7':
        body = <Serial7Step step={currentStep} answer={stepAnswer} onChange={setAnswer} />; break;
      case 'fluency':
        body = <FluencyStep step={currentStep} answer={stepAnswer} onChange={setAnswer} />; break;
      case 'memory-trial':
        body = <MemoryTrialStep step={currentStep} answer={stepAnswer} onChange={setAnswer} />; break;
      case 'recall':
        body = <RecallStep step={currentStep} answer={stepAnswer} onChange={setAnswer} />; break;
      default:
        body = null;
    }
  }

  return (
    <div className="moca-shell">
      <header className="moca-topbar">
        <button onClick={handleExit} className="moca-exit">
          <X size={15} strokeWidth={2.2} /> Exit
        </button>
        <div className="moca-progress-wrap">
          <div className="moca-progress-track">
            <div className="moca-progress-fill" style={{ width: `${progress * 100}%` }} />
          </div>
          <div className="moca-progress-label">
            {stepIndex + 1} / {ALL_STEPS.length}
          </div>
        </div>
        <div className="moca-running-score">
          {runningResult.total}<span className="moca-running-of">/{runningResult.max}</span>
        </div>
      </header>

      <main className="moca-main" key={currentStep.id}>
        {currentStep.kind !== 'intro' && (
          <div className="moca-step-eyebrow">
            <span className="moca-section-pill">{currentStep.section_title}</span>
            {currentStep.max > 0 && (
              <span className="moca-step-pts">{currentStep.max} {currentStep.max === 1 ? 'pt' : 'pts'}</span>
            )}
          </div>
        )}
        {/* In interactive mode the inner component supplies its own patient-
            facing heading via <PatientPrompt>, so we only render the outer
            provider-facing title for the classic mode. */}
        {currentStep.kind !== 'intro' && mode === 'provider' && (
          <h1 className="moca-step-title">{currentStep.headline}</h1>
        )}
        {body}
      </main>

      <footer className="moca-footer">
        <button
          onClick={goBack}
          disabled={isFirst}
          className="moca-btn moca-btn-ghost"
        >
          <ArrowLeft size={13} strokeWidth={2.5} /> Back
        </button>
        <div className="moca-footer-spacer" />
        {!hasScore && currentStep.max > 0 && (
          <button
            onClick={goNext}
            className="moca-btn moca-btn-ghost"
            title="Move on without scoring; you can come back to it."
          >
            Skip
          </button>
        )}
        <button
          onClick={goNext}
          className="moca-btn moca-btn-primary"
        >
          {isLast ? <>Review <ArrowRight size={14} strokeWidth={2.5} /></> : <>Continue <ArrowRight size={14} strokeWidth={2.5} /></>}
        </button>
      </footer>
    </div>
  );
}
